import {
	COMPANY_SECTORS,
	COMPANY_SIZES,
	DESCRIPTION_MAX,
	type EspaceOverview,
	TAGLINE_MAX,
} from "@shared/espace.types";
import { Building2, Lock } from "lucide-react";
import { Card, CardHeader } from "@/components/ds/Card";
import { EmptyState } from "@/components/ds/EmptyState";
import { NumberField, SelectField, TextAreaField, TextField } from "@/components/ds/Fields";
import { LinkButton } from "@/components/ds/LinkButton";
import { formatAddress } from "@/lib/address";

/**
 * La fiche de l'entreprise, en DEUX moitiés qui n'ont pas le même statut.
 *
 * Cet écran disait il y a peu « il n'y a rien à modifier ici, et c'est voulu ».
 * C'était vrai de la moitié administrative, et faux de tout le reste : une raison
 * sociale se déduit du SIREN, une façon de se présenter ne se déduit de rien.
 *
 * Ce qui n'a PAS changé, et qui ne changera pas : `siren`, `siret`, `name` et
 * l'adresse viennent de l'annuaire de l'État, portent le dédoublonnage, et restent
 * en lecture seule. Le serveur les REFUSE, il ne se contente pas de les ignorer.
 */
export function CompanyPage({
	overview,
	onSave,
}: {
	overview: EspaceOverview;
	/** Rend `false` quand l'enregistrement a échoué : le champ le dit alors à l'écran. */
	onSave: (patch: Record<string, unknown>) => Promise<boolean>;
}) {
	const { company, contact, schools } = overview;

	const administratif: [string, string | null][] = [
		["Raison sociale", company?.name ?? null],
		["SIREN", company?.siren ?? null],
		["SIRET du siège", company?.siret ?? null],
		["Adresse", company ? formatAddress(company) || null : null],
	];

	return (
		<div className="flex flex-col gap-5">
			<header>
				<h1 className="t-h1 text-mg-heading">Mon entreprise</h1>
				<p className="t-body mt-1 text-mg-secondary">
					Ce que vous écrivez ici, votre école le lit quand elle reçoit vos offres.
				</p>
			</header>

			{/*
			 * Sans entreprise rattachée, les champs ne s'enregistreraient JAMAIS : la
			 * route rend 404, et l'entreprise ne verrait qu'un « Non enregistré » de six
			 * secondes sans savoir quoi faire. L'état est atteignable — `company_id` est
			 * en `on delete set null`.
			 */}
			{company ? (
				<Card padded={false}>
					<CardHeader
						title="Votre présentation"
						subtitle="Chaque champ s'enregistre quand vous le quittez."
					/>
					<div className="grid gap-6 p-6 sm:grid-cols-2">
						<div className="sm:col-span-2">
							<TextField
								label="En une phrase"
								hint="Ce que vous faites, dit simplement. C'est la première ligne que l'école lit."
								max={TAGLINE_MAX}
								value={company.tagline ?? ""}
								onSave={(tagline) => onSave({ tagline })}
							/>
						</div>

						<div className="sm:col-span-2">
							<TextAreaField
								label="Votre entreprise"
								hint="Vos métiers, votre équipe, ce qui donne envie d'y faire son alternance."
								max={DESCRIPTION_MAX}
								value={company.description ?? ""}
								onSave={(description) => onSave({ description })}
							/>
						</div>

						<TextField
							label="Site web"
							hint="Avec https://"
							value={company.website ?? ""}
							onSave={(website) => onSave({ website })}
						/>

						<SelectField
							label="Secteur"
							options={COMPANY_SECTORS}
							value={company.sector ?? ""}
							onSave={(sector) => onSave({ sector: sector || null })}
						/>

						<SelectField
							label="Effectif"
							options={COMPANY_SIZES}
							value={company.size ?? ""}
							onSave={(size) => onSave({ size: size || null })}
						/>

						{/*
						 * Les bornes sont les MÊMES que celles du serveur
						 * (`routes/espace.ts`) : le champ natif écarte la faute de frappe
						 * avant l'aller-retour, la route reste seule juge. Elles étaient
						 * écrites en dur dans le composant local, et l'extraction vers
						 * `ds/Fields` les avait perdues en route.
						 */}
						<NumberField
							label="Année de création"
							min={1800}
							max={2100}
							value={company.foundedYear ?? null}
							onSave={(foundedYear) => onSave({ foundedYear })}
						/>
					</div>
				</Card>
			) : (
				<Card padded={false}>
					<EmptyState
						icon={<Building2 className="size-5" />}
						title="Aucune entreprise rattachée à votre accès"
						body="Déposez une offre : vous choisirez votre entreprise dans l'annuaire, et cette page deviendra la sienne."
						action={<LinkButton to="/espace/offres">Voir mes offres</LinkButton>}
					/>
				</Card>
			)}

			<Card padded={false}>
				<CardHeader title="Vos coordonnées" subtitle="Aucun mot de passe, aucun compte à gérer." />
				<div className="grid gap-6 p-6 sm:grid-cols-2">
					<TextField
						label="Votre nom"
						value={contact.fullName ?? ""}
						onSave={(fullName) => onSave({ fullName })}
					/>
					<TextField
						label="Téléphone"
						value={contact.phone ?? ""}
						onSave={(phone) => onSave({ phone })}
					/>
					<div className="sm:col-span-2">
						<span className="t-label text-mg-heading">Adresse email</span>
						<p className="t-body mt-1.5 text-mg-heading">{contact.email}</p>
						<p className="t-caption mt-1.5 text-mg-secondary">
							C'est votre clé d'accès : le code arrive dessus. Pour en changer, écrivez à votre
							école.
						</p>
					</div>
				</div>
			</Card>

			<Card padded={false}>
				<CardHeader
					title="Informations légales"
					subtitle="Elles viennent de l'annuaire des entreprises et ne se modifient pas ici."
					action={
						<span className="t-caption flex items-center gap-1.5 text-mg-secondary">
							<Lock className="size-3.5" />
							Lecture seule
						</span>
					}
				/>
				<dl className="grid gap-x-6 gap-y-4 p-5 sm:grid-cols-2">
					{administratif.map(([label, value]) => (
						<div key={label}>
							<dt className="t-caption text-mg-secondary">{label}</dt>
							<dd className="t-body-md mt-0.5 text-mg-heading">{value ?? "Non renseigné"}</dd>
						</div>
					))}
				</dl>
			</Card>

			{schools.length > 0 ? (
				<Card padded={false}>
					<CardHeader
						title={schools.length === 1 ? "Votre école" : "Vos écoles"}
						subtitle={
							schools.length === 1
								? "Celle avec qui vous avez déjà échangé."
								: "Toutes voient la même fiche."
						}
					/>
					<ul>
						{schools.map((school) => (
							<li
								key={school.slug}
								className="flex items-center gap-3 border-b border-mg-border-subtle px-5 py-3.5 last:border-b-0"
							>
								{school.logoUrl ? (
									<img
										src={school.logoUrl}
										alt=""
										className="size-8 rounded-lg border border-mg-border-subtle object-contain p-0.5"
									/>
								) : null}
								<span className="t-body-md text-mg-heading">{school.name}</span>
							</li>
						))}
					</ul>
				</Card>
			) : null}
		</div>
	);
}
